import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "card"
}
const _hoisted_2 = { class: "field" }
const _hoisted_3 = {
  key: 0,
  class: "p-error"
}
const _hoisted_4 = { class: "field" }
const _hoisted_5 = {
  key: 0,
  class: "p-error"
}
const _hoisted_6 = {
  key: 0,
  class: "field"
}
const _hoisted_7 = {
  key: 0,
  class: "p-error"
}
const _hoisted_8 = {
  key: 1,
  class: "field"
}
const _hoisted_9 = {
  key: 0,
  class: "p-error"
}
const _hoisted_10 = { class: "field" }
const _hoisted_11 = {
  key: 0,
  class: "p-error"
}
const _hoisted_12 = { class: "field" }
const _hoisted_13 = { class: "flex justify-content-end" }

import { computed, onMounted, ref, toRefs } from 'vue';
import useVuelidate from '@vuelidate/core';
import { email, required, requiredIf } from '@vuelidate/validators';
import Dialog from 'primevue/dialog';
import TreeSelect from 'primevue/treeselect';
import AppSelectButton from '@/components/AppSelectButton/AppSelectButton.vue';
import { EMIT, PERMISSIONS } from '@/enum';
import { useDateTime, usePersonTypes, useRouting, can } from '@/composables';
import {
  usePeopleStore,
  useAuthStore,
  usePeopleTypesStore,
  useTimezoneStore,
  useOrganizationStore,
  PersonStores,
} from '@/store';
import { ApiPromise, GeneralProfileInterface, OrganizationInterface, PersonCreateInterface } from '@/types';

/**
 * Define a routing instance for the utility.
 */
interface UserCreatePropsInterface {
  isEdit?: boolean;
  isAdmin?: boolean;
  id?: number | null;
  personTypeAcronym?: string;
  header?: string;
  saveButtonText?: string;
  isProfile?: boolean;
}

interface UserEditInterface {
  email?: string;
  externalId?: string;
  organizationId?: number;
  organization?: string;
  firstName: string;
  lastName: string;
  personTypeId: string | number;
  timezoneId?: string | number;
  timezone?: string;
}
/**
 * if eLuma tenant user we add these to the userTypes:
 * Billing Admin, Service Provider Supervisor,
 * Service Provider Admin,Support Admin and Super Admin.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'UserCreate',
  props: {
    isEdit: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    id: { default: null },
    personTypeAcronym: { default: '' },
    header: { default: 'Create User' },
    saveButtonText: { default: 'Create User' },
    isProfile: { type: Boolean, default: false }
  },
  emits: [EMIT.CANCEL, EMIT.EDIT],
  setup(__props: any, { emit: __emit }) {

const $routing = useRouting();

/**
 * Define the store instances.
 */
const peopleStore = usePeopleStore();
const peopleTypesStore = usePeopleTypesStore();
const timezoneStore = useTimezoneStore();
const orgStore = useOrganizationStore();
const profileStore = PersonStores.useUserProfileStore();
const authStore = useAuthStore();
const PEOPLE_TYPES = peopleTypesStore.PEOPLE_TYPES;
const $externalResults = ref({});

const props = __props;

const { isEdit, isAdmin, id, header, personTypeAcronym, saveButtonText, isProfile } = toRefs(props);

/**
 * Define all the available emit events.
 */
const emit = __emit;

/**
 * Whether or not to display the user create modal.
 */
const displayModal = ref(true);

/**
 * for the user types we only need for the tabs
 */
const userTypes = ref();

/**
 * support admin types
 */
const supportAdminUserTypes = ref([{ label: 'Support Admin', id: PEOPLE_TYPES.SUPPORT_ADMIN }]);

/**
 * super admin types
 */
const superAdminUserTypes = ref([{ label: 'Super Admin', id: PEOPLE_TYPES.SUPER_ADMIN }]);

/**
 * non-admin types
 */
const nonAdminTypes = ref([
  { label: 'Service Provider', id: PEOPLE_TYPES.CLINICIAN },
  { label: 'Case Manager', id: PEOPLE_TYPES.CASE_MANAGER },
  { label: 'Facilitator', id: PEOPLE_TYPES.FACILITATOR },
  { label: 'Parent', id: PEOPLE_TYPES.PARENT },
  { label: 'School Admin', id: PEOPLE_TYPES.SCHOOL_ADMIN },
]);

const eLumaUserTypes = ref([{ label: 'Billing Admin', id: PEOPLE_TYPES.BILLING_ADMIN }]);

/**
 * The data ref object used to create the request payload.
 */
const data = ref<UserEditInterface>({
  email: '',
  externalId: '',
  organizationId: '',
  organization: '',
  firstName: '',
  lastName: '',
  personTypeId: '',
  timezoneId: '',
  timezone: '',
});

const isStudent = computed<boolean>(() => {
  const personType = data.value.personTypeId;

  if (personType === PEOPLE_TYPES.STUDENT) {
    return usePersonTypes.isStudent(PEOPLE_TYPES.STUDENT);
  }

  return false;
});

const isBillingAdmin = computed(() => {
  return data.value.personTypeId === PEOPLE_TYPES.BILLING_ADMIN;
});

const editLoading = computed<boolean>(() => (isEdit.value ? !!profileStore.loading : false));

const modalLoading = ref(false);

/**
 * is the admin a super admin?
 */
const isSuperAdmin = computed<boolean>(() => authStore.acronymType === PEOPLE_TYPES.SUPER_ADMIN);

/**
 * is the admin a support admin?
 */
const isSupportAdmin = computed<boolean>(() => authStore.acronymType === PEOPLE_TYPES.SUPPORT_ADMIN);

/**
 * The required validation rules.
 */
const rules = computed(() => {
  return {
    email: { email },
    externalId: {
      requiredIfStudent: requiredIf(isStudent),
    },
    organization: {
      requiredIfStudent: requiredIf(isStudent),
    },
    firstName: { required },
    lastName: { required },
    personTypeId: { required },
    timezoneId: {},
  };
});

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, data, { $externalResults });

const requiredIfStudent = computed(() => {
  v$.value.externalId.$touch();
  return isStudent.value ? 'required' : '';
});

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted = ref<boolean>(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * master organization for adding eluma super, support and billing admins
 */
const masterOrganization = ref<OrganizationInterface>();
/**
 * Initialize the necessary data in order to build out the component.
 */
const init = async () => {
  modalLoading.value = true;
  if (isAdmin.value) masterOrganization.value = await orgStore.getOrganizationByName('eLuma');
  if (isEdit.value) await profileStore.getProfile(id.value as number);
  if (can([PERMISSIONS.PEOPLE_TYPES_INDEX])) await peopleStore.getPeopleTypes();
  if (can([PERMISSIONS.TIMEZONES_INDEX])) await timezoneStore.getTimezones();
  if (can([PERMISSIONS.ORGANIZATIONS_INDEX]) || isBillingAdmin.value) await orgStore.getParentsOrganizations();
  await defineDefaults();
  await createUserTypesArray();
  modalLoading.value = false;
};

/**
 * Define the request create payload.
 */
const payload = computed(() => {
  const value = data.value;
  const firstName = value.firstName.trim();
  const lastName = value.lastName.trim();
  return {
    person_type_id: findActualPersonTypeId(value.personTypeId as string),
    first_name: firstName,
    last_name: lastName,
    display_name: `${firstName} ${lastName}`,
    full_name: `${firstName} ${lastName}`,
    abbrev_name: `${firstName.charAt(0)}. ${lastName}`,
    external_id: value.externalId,
    email: value.email,
    timezone_id: value.timezoneId,
    timezone: value.timezone,
    organization_id: value.organizationId,
  };
});

/**
 * take the selected person type acronym and find the person type ID
 */
const findActualPersonTypeId = (personTypeAcronym: string) => {
  const type = peopleTypesStore.types.find((item) => item.acronym === personTypeAcronym);
  return type?.id;
};

/**
 * Define all default values that will pre-populate the form.
 */
const defineDefaults = async () => {
  const timezone = useDateTime().findTimezone();
  v$.value.$invalid = true;

  if (timezone) {
    data.value.timezoneId = timezone.id.toString();
    data.value.timezone = timezone.name;
  }

  if (isEdit.value) {
    setDataValues();
  }
};

/**
 * if we are editing we need to set the data values
 */
const setDataValues = () => {
  const value = profileStore.person;
  if (value) {
    data.value = {
      personTypeId: personTypeAcronym.value,
      firstName: value.first_name,
      lastName: value.last_name,
      email: value?.email,
      timezoneId: value?.timezone_id,
      timezone: value?.timezone,
    };
  }
};

/**
 * create the UserTypes array based on the values coming in, isEluma, and isAdmin
 */
const createUserTypesArray = () => {
  if (isAdmin.value) {
    if (isEdit.value && !isSuperAdmin.value) return;

    userTypes.value = supportAdminUserTypes.value;
    //if its a super admin then allow them to add super admins
    if (isSuperAdmin.value) userTypes.value = userTypes.value.concat(superAdminUserTypes.value);
  } else {
    userTypes.value = nonAdminTypes.value;
    if (authStore.currentInstanceIsMasterTenant && (isSuperAdmin.value || isSupportAdmin.value))
      userTypes.value = userTypes.value.concat(eLumaUserTypes.value);
  }
  //auto selet the first one in the array
  data.value.personTypeId = userTypes.value[0].id;
};

/**
 * get the key of the org tree select object for the ID
 */
const getOrganizationId = (val) => (data.value.organizationId = parseInt(Object.keys(val)));

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles: string): string => {
  const validation =
    (v$.value[key].$invalid && submitted.value) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * After a successful save, redirect the user to the new user account.
 */
const doSave = (response: ApiPromise) => {
  doToast();
  setTimeout(() => {
    const route = isAdmin.value ? `/admin/administrators/${response.id.toString()}` : `/user/${response.id.toString()}`;
    $routing.redirectTo(route);
  }, 3000);
};

/**
 * After a successful edit, close the modal
 */
const doUpdate = () => {
  if (isProfile.value) {
    authStore.getAuthUserProfile();
  }
  doToast('The admin has been updated');
  emit(EMIT.EDIT);
};

/**
 * Display the toast message.
 */
const doToast = (message = 'The new account has been saved', error = false) => {
  const summary = error ? 'Error' : 'Saved';
  authStore.doToast(message, summary, error);
};

/**
 * Do the following after a failed save request.
 * Reset the loading status indicator.
 * Set the error ref object with the error response.
 * Display the toast message.
 */
const doFail = (response: ApiPromise) => {
  submitted.value = false;
  doToast(response.data.message, true);
  emit(EMIT.CANCEL);
};

/**
 * Handle the cancellation of the user create modal.
 */
const onCancel = () => emit(EMIT.CANCEL);

/**
 * Handle the client side validation, followed by saving the
 * information for the new user account.
 */

const isPeopleTypeStudent = computed(() => {
  const peopleTypeStudent = peopleStore.peopleTypes.filter((peopleType) => peopleType.acronym === PEOPLE_TYPES.STUDENT);

  if (peopleTypeStudent.length > 0) {
    return payload.value.person_type_id === peopleTypeStudent[0].id;
  }

  return false;
});

const onSave = async () => {
  submitted.value = true;
  let payloadToRequest = payload.value;

  // Don't allow a submit event unless the fields are valid.
  if (!(await v$.value.$validate())) return;

  if (!isPeopleTypeStudent.value) {
    const {
      person_type_id,
      first_name,
      last_name,
      display_name,
      full_name,
      abbrev_name,
      external_id,
      email,
      timezone_id,
      timezone,
    } = payload.value;

    payloadToRequest = {
      person_type_id,
      first_name,
      last_name,
      display_name,
      full_name,
      abbrev_name,
      external_id,
      email,
      timezone_id,
      timezone,
    };
  }

  if (isEdit.value) {
    profileStore.id = id.value as number;
    profileStore
      .updateGeneralInfo(payloadToRequest as GeneralProfileInterface)
      .then((response) => doUpdate(response))
      .catch((response) => doFail(response));
  } else {
    if (isAdmin.value) payloadToRequest.organization_id = masterOrganization.value?.id;

    peopleStore
      .createPerson(payloadToRequest as PersonCreateInterface)
      .then((response) => doSave(response))
      .catch((response) => {
        doFail(response);
        displayModal.value = false;
      });
  }
};

/**
 * Handle the filtering of timezones based on zipcode.
 * The search will not begin until at least 4 characters
 * have been entered.
 */
const onTimezoneFilter = (event) => {
  const zipcode = event.value;
  const hasValue = zipcode.length >= 4;

  if (hasValue) timezoneStore.searchTimezonesByZipcode(zipcode);
};

const selectPersonType = (id: string | number) => {
  data.value.personTypeId = id;
};

return (_ctx: any,_cache: any) => {
  const _component_InputText = _resolveComponent("InputText")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_Loader = _resolveComponent("Loader")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: displayModal.value,
    "onUpdate:visible": _cache[7] || (_cache[7] = ($event: any) => ((displayModal).value = $event)),
    modal: true,
    header: _unref(header),
    draggable: false,
    breakpoints: { '960px': '75vw', '640px': '100vw' },
    style: { maxWidth: '600px', width: '100%' },
    onHide: onCancel
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_13, [
        _createVNode(_component_AppButton, {
          type: "secondary",
          text: "Cancel",
          class: "mr-2",
          disabled: submitted.value,
          onClick: onCancel
        }, null, 8, ["disabled"]),
        _createVNode(_component_AppButton, {
          type: "primary",
          text: _unref(saveButtonText),
          disabled: _unref(v$).$invalid || submitted.value,
          onClick: onSave
        }, null, 8, ["text", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      (!editLoading.value && !modalLoading.value)
        ? (_openBlock(), _createElementBlock("form", _hoisted_1, [
            _createVNode(AppSelectButton, {
              items: userTypes.value,
              "selected-item": data.value.personTypeId,
              class: "pb-3",
              onSelectItem: _cache[0] || (_cache[0] = ($event: any) => (selectPersonType($event.id)))
            }, null, 8, ["items", "selected-item"]),
            _createElementVNode("div", _hoisted_2, [
              _cache[8] || (_cache[8] = _createElementVNode("label", {
                class: "required",
                for: "firstName"
              }, "First Name", -1)),
              _createVNode(_component_InputText, {
                id: "firstName",
                modelValue: _unref(v$).firstName.$model,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).firstName.$model) = $event)),
                type: "text",
                placeholder: "First Name",
                disabled: submitted.value,
                class: _normalizeClass(fieldStyles('firstName', 'w-full'))
              }, null, 8, ["modelValue", "disabled", "class"]),
              (errors.value && errors.value.firstName)
                ? (_openBlock(), _createElementBlock("small", _hoisted_3, _toDisplayString(errors.value.firstName[0]), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _cache[9] || (_cache[9] = _createElementVNode("label", {
                class: "required",
                for: "lastName"
              }, "Last Name", -1)),
              _createVNode(_component_InputText, {
                id: "lastName",
                modelValue: _unref(v$).lastName.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).lastName.$model) = $event)),
                type: "text",
                placeholder: "Last Name",
                disabled: submitted.value,
                class: _normalizeClass(fieldStyles('lastName', 'w-full'))
              }, null, 8, ["modelValue", "disabled", "class"]),
              (errors.value && errors.value.lastName)
                ? (_openBlock(), _createElementBlock("small", _hoisted_5, _toDisplayString(errors.value.lastName[0]), 1))
                : _createCommentVNode("", true)
            ]),
            (isStudent.value || isBillingAdmin.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("label", {
                    class: _normalizeClass(requiredIfStudent.value),
                    for: "org"
                  }, "Organization", 2),
                  _createVNode(_unref(TreeSelect), {
                    id: "org",
                    modelValue: _unref(v$).organization.$model,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).organization.$model) = $event)),
                    options: _unref(orgStore).organizationParents,
                    "selection-mode": "single",
                    loading: _unref(orgStore).loading,
                    placeholder: "Select organization",
                    disabled: submitted.value,
                    class: "w-full",
                    onChange: getOrganizationId
                  }, null, 8, ["modelValue", "options", "loading", "disabled"]),
                  (_unref(v$).organization.$error)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_7, "Organization is required"))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (!_unref(isAdmin))
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("label", {
                    class: _normalizeClass(requiredIfStudent.value),
                    for: "externalId"
                  }, _toDisplayString(isStudent.value ? 'SSID' : 'External ID'), 3),
                  _createVNode(_component_InputText, {
                    id: "externalId",
                    modelValue: _unref(v$).externalId.$model,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).externalId.$model) = $event)),
                    type: "text",
                    placeholder: "example: 123456789",
                    disabled: submitted.value,
                    class: _normalizeClass(fieldStyles('externalId', 'w-full'))
                  }, null, 8, ["modelValue", "disabled", "class"]),
                  (errors.value && errors.value.externalId)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_9, _toDisplayString(errors.value.externalId[0]), 1))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).externalId.$externalResults, (error) => {
                    return (_openBlock(), _createElementBlock("small", {
                      key: error.$uid,
                      class: "p-error"
                    }, _toDisplayString(error.$message), 1))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_10, [
              _cache[10] || (_cache[10] = _createElementVNode("label", { for: "email" }, "Email", -1)),
              _createVNode(_component_InputText, {
                id: "email",
                modelValue: _unref(v$).email.$model,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(v$).email.$model) = $event)),
                type: "text",
                placeholder: "example@email.com",
                disabled: submitted.value,
                class: "w-full"
              }, null, 8, ["modelValue", "disabled"]),
              (errors.value && errors.value.email)
                ? (_openBlock(), _createElementBlock("small", _hoisted_11, _toDisplayString(errors.value.email[0]), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_12, [
              _cache[11] || (_cache[11] = _createElementVNode("label", { for: "timezone" }, "Timezone", -1)),
              _createVNode(_component_Dropdown, {
                id: "timezone",
                modelValue: _unref(v$).timezoneId.$model,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(v$).timezoneId.$model) = $event)),
                options: _unref(timezoneStore).timezones,
                "option-label": "name",
                "option-value": "id",
                loading: _unref(timezoneStore).loading,
                placeholder: "Select timezone",
                filter: true,
                "filter-placeholder": "Search by zipcode",
                "filter-fields": ['zipcode', 'name'],
                disabled: submitted.value,
                class: "w-full",
                onFilter: onTimezoneFilter
              }, null, 8, ["modelValue", "options", "loading", "disabled"])
            ])
          ]))
        : (_openBlock(), _createBlock(_component_Loader, {
            key: 1,
            relative: true,
            class: "flex justify-content-center align-items-center h-24rem"
          }))
    ]),
    _: 1
  }, 8, ["visible", "header"]))
}
}

})